import {
    type CSSProperties,
    type FC,
    type MutableRefObject,
    type ReactElement,
    type ReactNode,
    useMemo,
    useRef,
} from 'react';
import classnames from 'classnames';

import { ItemMediaSpinner } from 'dibs-elements/exports/ItemMediaSpinner';
import { IS_IOS_WEBKIT, useFullscreen } from 'dibs-elements/exports/useFullscreen';
import ArrowClockwise from 'dibs-icons/exports/legacy/ArrowClockwise';
import Pause from 'dibs-icons/exports/legacy/Pause';
import PlayButton from 'dibs-icons/exports/legacy/PlayButton';
import Volume from 'dibs-icons/exports/legacy/Volume';
import VolumeOff from 'dibs-icons/exports/legacy/VolumeOff';
import ZoomIn from 'dibs-icons/exports/legacy/ZoomIn';
import ZoomOut from 'dibs-icons/exports/legacy/ZoomOut';
import Moon from 'dibs-icons/exports/legacy/Moon';
import Sun from 'dibs-icons/exports/legacy/Sun';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { useMediaDarkMode } from 'dibs-react-hooks/exports/useMediaDarkMode';

import { type TokenListGuard } from '../Common/TokenListGuard';

import styles from './main.scss';

const messages = defineMessages({
    fullScreen: { id: 'Video.fullScreen', defaultMessage: 'Full screen' },
    exitFullScreen: {
        id: 'Video.exitFullScreen',
        defaultMessage: 'Exit full screen',
    },
    replay: { id: 'Video.replay', defaultMessage: 'Replay' },
    play: { id: 'Video.play', defaultMessage: 'Play' },
    pause: { id: 'Video.pause', defaultMessage: 'Pause' },
    mute: { id: 'Video.mute', defaultMessage: 'Mute' },
    unmute: { id: 'Video.unmute', defaultMessage: 'Unmute' },
    darkMode: { id: 'Video.darkMode', defaultMessage: 'Dark mode' },
    lightMode: { id: 'Video.lightMode', defaultMessage: 'Light mode' },
});

type ControlList = 'play' | 'replay' | 'audio' | 'fullscreen' | 'darkmode';

type ControlProps<T extends string> = {
    id: T;
    onClick?: () => void;
    ariaLabel: string;
    children: ReactNode;
    isDarkMode?: boolean;
};

type LayoutProps =
    | {
          position?: 'center';
          direction?: 'row';
      }
    | {
          position?: 'right' | 'rightEdge';
          direction?: 'row' | 'column';
      };

export type CommonControlsProps<T extends string = '', U extends string = ''> = {
    className?: string;
    controls?: boolean | 'always' | 'never';
    borderRadius?: number;
    separator?: boolean;
    shadow?: boolean;
    placeholder?: 'shimmer' | 'spinner';
    poster?: string;
    onFullScreenOpen?: () => void;
    onFullScreenClose?: () => void;
    additionalControls?: ControlProps<U>[];
    controlsList: TokenListGuard<T, `${ControlList} ${U}`>;
} & LayoutProps;

type MediaControlsFC = <T extends string = '', U extends string = ''>(
    args: {
        children?: ReactNode;
        mediaRef?: MutableRefObject<HTMLElement | null>;
        mediaContainerRef?: MutableRefObject<HTMLElement | null>;
        isLoading?: boolean;
        isFullscreen?: boolean;
        isMuted?: boolean;
        isPaused?: boolean;
        onPlay?: () => void;
        onFullscreen?: () => void;
        onReplay?: () => void;
        onMute?: () => void;
    } & CommonControlsProps<T, U>
) => ReactElement;

const Control: FC<{
    onClick?: () => void;
    ariaLabel?: string;
    children: ReactNode;
    isDarkMode?: boolean;
}> = ({ onClick, ariaLabel, children, isDarkMode = false }) => (
    <>
        {/* eslint-disable-next-line react/forbid-elements */}
        <button
            type="button"
            title={ariaLabel}
            onClick={onClick}
            aria-label={ariaLabel}
            className={classnames(styles.control, {
                [styles.darkMode]: isDarkMode,
            })}
        >
            {children}
        </button>
    </>
);

export const MediaControls: MediaControlsFC = ({
    additionalControls: additionalControlsProp = [],
    borderRadius,
    children,
    className,
    controlsList: controlsListProp,
    shadow,
    isLoading,
    isMuted,
    isPaused,
    mediaContainerRef,
    mediaRef,
    onFullScreenClose,
    onFullScreenOpen,
    onMute,
    onPlay,
    onReplay,
    placeholder,
    poster,
    separator = false,
    controls,
    position = 'right',
    direction = 'column',
}) => {
    const { formatMessage } = useIntl();
    const containerRef = useRef<HTMLElement | null>(null);
    const localRef = mediaContainerRef || containerRef;

    // iOS safari must use fullscreen request on video element
    const fullscreenRef = useMemo(
        () => (IS_IOS_WEBKIT ? mediaRef : localRef),
        [localRef, mediaRef]
    );

    const style = poster ? ({ ['--poster']: `url(${poster})` } as CSSProperties) : undefined;

    const { isFullscreen, handleFullscreenToggle } = useFullscreen(
        fullscreenRef || { current: null },
        true,
        onFullScreenOpen,
        onFullScreenClose
    );
    const [isDarkMode, toggleDarkMode] = useMediaDarkMode(isFullscreen);

    const idList = controlsListProp?.split(' ') || [];

    let controlsList = [
        {
            id: 'play',
            onClick: onPlay,
            ariaLabel: formatMessage(isPaused ? messages.play : messages.pause),
            children: isPaused ? <PlayButton /> : <Pause />,
            isDarkMode: isDarkMode && idList.includes('darkmode'),
        },
        {
            id: 'replay',
            onClick: onReplay,
            ariaLabel: formatMessage(messages.replay),
            children: <ArrowClockwise />,
            isDarkMode: isDarkMode && idList.includes('darkmode'),
        },
        {
            id: 'audio',
            onClick: onMute,
            ariaLabel: formatMessage(isMuted ? messages.unmute : messages.mute),
            children: isMuted ? <VolumeOff /> : <Volume />,
            isDarkMode: isDarkMode && idList.includes('darkmode'),
        },
        {
            id: 'darkmode',
            onClick: toggleDarkMode,
            ariaLabel: formatMessage(isDarkMode ? messages.lightMode : messages.darkMode),
            children: isDarkMode ? <Sun /> : <Moon />,
            isDarkMode: isDarkMode && idList.includes('darkmode'),
        },
        {
            id: 'fullscreen',
            onClick: handleFullscreenToggle,
            ariaLabel: formatMessage(isFullscreen ? messages.exitFullScreen : messages.fullScreen),
            children: isFullscreen ? <ZoomOut /> : <ZoomIn />,
            isDarkMode: isDarkMode && idList.includes('darkmode'),
        },
        ...additionalControlsProp.map(control => ({
            ...control,
            isDarkMode: isDarkMode && idList.includes('darkmode'),
        })),
    ];

    // darkmode only available for fullscreen
    if (!isFullscreen) {
        controlsList = controlsList.filter(control => control.id !== 'darkmode');
    }

    let controlsComponents;
    if (idList.length > 0) {
        controlsComponents = idList.map(reorderId => {
            const foundControl = controlsList.find(
                defaultControl => defaultControl.id === reorderId
            );
            return foundControl ? <Control key={foundControl.id} {...foundControl} /> : null;
        });
    } else {
        controlsComponents = controlsList.map(({ id, ...rest }) => <Control key={id} {...rest} />);
    }

    return (
        <figure
            ref={localRef}
            style={style}
            className={classnames(styles.container, className, {
                [styles.fullscreen]: isFullscreen,
                [styles.darkMode]: isDarkMode && idList.includes('darkmode'),
            })}
        >
            {placeholder === 'spinner' && <ItemMediaSpinner isOpen={isLoading} />}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            {children}
            {controls !== 'never' && (
                <div
                    style={
                        {
                            ['--controls-border-radius']: borderRadius?.toString()?.concat('px'),
                        } as CSSProperties
                    }
                    className={classnames(styles.controlList, {
                        [styles.column]: direction === 'column',
                        [styles.row]: direction === 'row',
                        [styles.right]: position === 'right',
                        [styles.rightEdge]: position === 'rightEdge',
                        [styles.center]: position === 'center',
                        [styles.shadow]: shadow,
                        [styles.hideControls]: controls === false,
                        [styles.separator]: separator,
                        [styles.showControlsAlways]: controls === 'always',
                        [styles.darkMode]: isDarkMode && idList.includes('darkmode'),
                    })}
                >
                    {controlsComponents}
                </div>
            )}
        </figure>
    );
};
